import { contentTopicRequest } from '../../questionBank/qbankNavigation';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from '@tanstack/react-query';
import { podcastDetailFlow, podcastNeedsPurchase, podcastRouteFlow, promptPodcastPurchase } from './podcastAccess';
import DOMPurify from "dompurify";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import MyWalletSidebar from "../MyWalletSidebar";
import HeaderProfileSection from "../HeaderProfileSection";
import AudioPlayer from "./AudioPlayer";
import { PodcastNotesModal, PodcastReportsModal } from "./PodcastEngagementModals";
import PodcastViewModal from "./PodcastViewModal";
import { useSidebarData } from "../../hooks/useSidebarData";
import { usePodcastDetail } from "../../hooks/usePodcastDetail";
import { resolveQuestionBankStateId } from "../../questionBank/stateUtils";
import { getStoredQuestionBankFlowState, persistQuestionBankFlowState } from "../../questionBank/qbankFlowState";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import "./AudioPodcast.css";
import { CONTENT_ROUTES } from "../contentRoutes";

const PODCAST_CATEGORY_ID = 12;

const PodcastDetailShimmer = () => <div className="podcastDetailShimmer" aria-label="Loading podcast" aria-busy="true">
  <div className="podcastDetailShimmer__title" />
  <div className="podcastDetailShimmer__subtitle" />
  <div className="podcastDetailShimmer__player">
    <div className="podcastDetailShimmer__actions"><span /><span /><span /></div>
    <div className="podcastDetailShimmer__controls"><span /><span /><span /></div>
    <div className="podcastDetailShimmer__progress" />
    <div className="podcastDetailShimmer__stats">{Array.from({ length: 5 }).map((_, index) => <span key={index} />)}</div>
  </div>
  <div className="podcastDetailShimmer__sectionTitle" />
  <div className="podcastDetailShimmer__tags"><span /><span /><span /></div>
  <div className="podcastDetailShimmer__sectionTitle podcastDetailShimmer__sectionTitle--notes" />
  <div className="podcastDetailShimmer__line" /><div className="podcastDetailShimmer__line" /><div className="podcastDetailShimmer__line podcastDetailShimmer__line--short" />
</div>;

const AudioPodcast = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useQueryClient();
  const purchaseBusy = useRef(false);
  const { data: sidebarData = null } = useSidebarData();
  const flowState = useMemo(() => podcastRouteFlow(location.state, getStoredQuestionBankFlowState()), [location.state]);
  const stateId = resolveQuestionBankStateId({ locationState: flowState, sidebarData });
  const subjectId = Number(flowState?.selectedSubject?.id || flowState?.subject_id || 0);
  const podcastId = Number(flowState?.podcast_id || flowState?.podcastId || flowState?.selectedPodcast?.id || 0);
  const appliedFilters = useMemo(() => flowState?.listQueryState?.appliedFilters || {}, [flowState]);
  const params = useMemo(() => ({ states_id: stateId, category_id: PODCAST_CATEGORY_ID, subject_id: subjectId, audio_podcast_id: podcastId, ...contentTopicRequest(flowState), ...(flowState.languages ? { languages: flowState.languages } : {}), ...(['F', 'P'].includes(flowState.free_paid || flowState.freeStatus) ? { free_paid: flowState.free_paid || flowState.freeStatus } : {}), ...(appliedFilters.languages ? { languages: appliedFilters.languages } : {}), ...(appliedFilters.personal_notes ? { personal_notes: appliedFilters.personal_notes } : {}), ...(appliedFilters.read_unread ? { read_unread: appliedFilters.read_unread } : {}), ...(appliedFilters.free_paid_filter ? { free_paid: appliedFilters.free_paid_filter } : {}), ...(appliedFilters.bookmark_status ? { bookmark_status: 1 } : {}), ...(appliedFilters.report_status ? { report_status: 1 } : {}), ...(flowState?.listQueryState?.searchTerm?.trim() ? { search_term: flowState.listQueryState.searchTerm.trim() } : {}) }), [appliedFilters, flowState, podcastId, stateId, subjectId]);
  const { data, isLoading, isError, error, refetch } = usePodcastDetail(params, { enabled: Boolean(stateId && subjectId && podcastId) });
  const podcast = data?.podcast || null;
  const [activeModal, setActiveModal] = useState(null);
  const purchase = async (coinsOnly = false) => {
    if (!podcast || purchaseBusy.current) return;
    purchaseBusy.current = true;
    try {
      await promptPodcastPurchase(podcast, { ...flowState, stateId, subjectId,
        subscriptionBackTarget: { pathname: CONTENT_ROUTES.podcastDetail, state: flowState } }, navigate, async () => {
        await Promise.all([
          client.invalidateQueries({ queryKey: ['sidebarData'] }),
          client.invalidateQueries({ queryKey: ['podcastList'] }),
        ]);
        const result = await refetch({ throwOnError: true });
        if (!result.data?.podcast || podcastNeedsPurchase(result.data.podcast)) throw new Error('Podcast access has not been confirmed. Please try again.');
      }, coinsOnly);
    } finally { purchaseBusy.current = false; }
  };
  const back = () => flowState?.qbankBackTarget?.pathname ? navigate(flowState.qbankBackTarget.pathname, { replace: true, state: flowState.qbankBackTarget.state }) : navigate(CONTENT_ROUTES.podcasts, { replace: true, state: flowState });
  const navigateEpisode = (nextPodcastId) => {
    if (!nextPodcastId) return;
    const nextState = podcastRouteFlow(podcastDetailFlow({ id: Number(nextPodcastId) }, flowState));
    persistQuestionBankFlowState(nextState);
    navigate(CONTENT_ROUTES.podcastDetail, { replace: true, state: nextState });
  };

  useEffect(() => { persistQuestionBankFlowState(flowState); }, [flowState]);

  return <>
    <style>{`
    .rapid-fire-card {
        padding: 20px 0px 10px;
    }
   `}</style>
    <HomeDashboardLayout showHeader={false} variant="podcast-detail-layout">
      <div className="qbl-page qbankList-page page-padding"><div className="start-quiz qbl-main instruction-main">
        <div className="hideProfile-page"><div className="categories-page-header mb-0"><div><button type="button" className="categories-back-button" onClick={back} aria-label="Go back"><i className="bi bi-chevron-left" /></button></div><div /><div><HeaderProfileSection /></div></div></div>
        {isLoading ? <PodcastDetailShimmer /> : null}
        {isError ? <div className="choose-state__status choose-state__status--error">{error?.response?.data?.message || error?.message || "Unable to load this podcast."}</div> : null}
        {!isLoading && !isError && !podcast ? <div className="choose-state__status">Podcast details are unavailable.</div> : null}
        {podcast && !isError ? <>
         <div className="rapid-fire-card"> <div className="rapid-fire-header ex-header"><h1 className="qb-title">{podcast.title}</h1></div>
          <p className="game-description">{[data?.stateName, data?.categoryName, data?.subjectName].filter(Boolean).join(" / ")}</p></div>
          <AudioPlayer key={podcast.id} podcast={podcast} subjectId={subjectId} onOpenModal={setActiveModal} onNavigateEpisode={navigateEpisode} onPurchase={() => purchase()} onCoinUnlock={() => purchase(true)}>
          {!podcastNeedsPurchase(podcast) && <div className="spAudio__content">
            {podcast.topics.length ? <section className="spAudio__topicSection"><h3 className="spAudio__sectionTitle">Topic Queue</h3><div className="spAudio__topicList">{podcast.topics.map((topic) => <span className="spAudio__topicTag" key={topic?.id || topic?.title}><span className="spAudio__tagIcon"><i className="bi bi-tag-fill" /></span><span>{topic?.title || topic?.name}</span></span>)}</div></section> : null}
            {podcast.description ? <section className="spAudio__notesSection"><h3 className="spAudio__sectionTitle">Episode Notes</h3><div className="spAudio__notesText" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(podcast.description) }} /></section> : null}
          </div>}
          </AudioPlayer>
        </> : null}
        <PodcastViewModal open={activeModal === "view"} onClose={() => setActiveModal(null)} podcast={podcast} />
        <PodcastNotesModal open={activeModal === "notes"} onClose={() => setActiveModal(null)} podcast={podcast} />
        <PodcastReportsModal open={activeModal === "report"} onClose={() => setActiveModal(null)} podcast={podcast} />
      </div></div>
      <MyWalletSidebar withTopOffset={false} />
    </HomeDashboardLayout>
  </>;
};

export default AudioPodcast;
